import { PeriodTabs } from './PeriodTabs';
import { InstrumentPicker } from './InstrumentPicker';
import { EASTMONEY_ONLY } from '../../lib/instruments';
import { OverlayConfig, PaneSelector } from './OverlayConfig';
import { MAIN_CHART_MIN, MOBILE_CHART_HEIGHT_VH, READOUT_HEIGHT, solveChartSpace } from '../../lib/layout';
import { useLayoutPreset } from '../layout/layoutPreset';
import { useSelection } from '../../lib/selection';
import { ChartCanvas } from './ChartCanvas';
import { ReadoutBar } from './ReadoutBar';
import { api, type Bar, type MinuteDaysResponse, type MinuteResponse, type ValuationPoint } from '../../lib/api';
import { useAsync } from '../../lib/dataHooks';
import { sma, bollinger } from '../../lib/indicators';
import { getChartTheme, upDown } from '../../lib/chartTheme';
import { MA_PERIODS, type MaPeriod, type OverlayId, type PaneId, type Period, type ValuationBasis, type ValuationKind } from '../../lib/app';
import { readSetting, writeSetting } from '../../lib/settings';
import { useCallback, useMemo, useRef, useState } from 'react';
import { isTradingSession, useNow } from '../../lib/hooks';

/** 分时类周期走 tick 链路，其余走日K 聚合链路 */
const isMinutePeriod = (p: Period) => p === 'MIN1' || p === 'MIN5';
const klinePeriod = (p: Period): 'D' | 'W' | 'M' | 'Y' =>
  p === 'M' ? 'M' : p === 'Y' ? 'Y' : p === 'W' ? 'W' : 'D';

/**
 * 图表区 —— 接入 /api/kline 与 /api/minute（需求 §F2）
 *
 * 保留 UI-A 已落实的垂直空间求解（solveChartSpace）与 0–3 副图槽位逻辑，
 * 主图/副图改为 lightweight-charts 真实渲染。
 */
export function ChartZone({ theme }: { theme: 'dark' | 'light' }) {
  const { instrument, period, effectiveDate, setDate } = useSelection();
  const { preset, onCyclePreset, isMobile } = useLayoutPreset();
  /* 盘中轮询：每分钟前后端各该一次（前端拉、后端抓）。
     会话判定复用 hooks.ts 的 isTradingSession（北京时间窗口）。
     useNow 每 30s 触发一次重算 —— 开盘/收盘临界处最多延迟半分钟。 */
  const now = useNow(30_000);
  const inSession = isTradingSession(now, instrument.market ?? 'CN');
  const POLL_MS = inSession ? 60_000 : 0;
  /** 窄屏取视口比例，桌面取档位固定 px（lightweight-charts 必须有确定高度） */
  const chartH = isMobile
    ? Math.round(window.innerHeight * MOBILE_CHART_HEIGHT_VH)
    : preset.chartHeightPx;
  const [panes, setPanes] = useState<(PaneId | null)[]>(['VOL', null, null]);
  /**
   * 分时的副图槽位**独立**于日K 的 panes：
   * 分时仅 VOL 可用 —— 若共用一组槽位，用户在 K线勾的 MACD/RSI 会在分时
   * 下不可用，而分时勾 VOL 又会覆盖回 K线(切回去 MACD 丢失)。
   * 槽位形状与 panes 一致（固定长度 3，null 占位）。
   */
  const [minutePanes, setMinutePanes] = useState<(PaneId | null)[]>(['VOL', null, null]);
  /** 十字线当前悬停的柱；null = 无光标（读数条回落显示最新概览） */
  const [crossDate, setCrossDate] = useState<string | null>(null);
  /** 分时十字线悬停的分钟点（头部点位/涨跌幅跟随用）；null = 离开 */
  const [crossMinute, setCrossMinute] = useState<{ dayIndex: number; hhmm: string } | null>(null);
  /** 去重：crosshairMove 每次 mousemove 都回调，同点不重复 setState */
  const crossMinuteKeyRef = useRef<string | null>(null);
  const handleCrossMinute = useCallback(
    (pt: { dayIndex: number; hhmm: string } | null) => {
      const key = pt ? `${pt.dayIndex}:${pt.hhmm}` : null;
      if (key === crossMinuteKeyRef.current) return;
      crossMinuteKeyRef.current = key;
      setCrossMinute(pt);
    },
    [],
  );
  const [overlays, setOverlays] = useState<Set<OverlayId>>(new Set<OverlayId>(['ma']));
  /**
   * MA 显示哪些周期。
   *
   * 改前是 ChartCanvas 里硬编码的 `[5,10,20,60,120]` 全部画出、不可选
   * （OverlayConfig 末尾那个「5▾」是无 onClick 的静态文本）。默认值与
   * 改前**完全一致**，即本次不改变任何用户的默认观感，只是把选择权交出去。
   */
  const [maPeriods, setMaPeriods] = useState<MaPeriod[]>(
    () => readSetting<MaPeriod[]>('chart.maPeriods', [...MA_PERIODS]),
  );
  const changeMaPeriods = (next: MaPeriod[]) => {
    setMaPeriods(next);
    writeSetting('chart.maPeriods', next);
  };
  const [valBasis, setValBasis] = useState<ValuationBasis>('pe2');
  const [valKind, setValKind] = useState<ValuationKind>('pe');

  /* ── 数据 ── */
  const isMin1Period = period === 'MIN1';
  const isMin5Period = period === 'MIN5';
  const isMinPeriodFlag = isMin1Period || isMin5Period;

  const minuteQ = useAsync<MinuteResponse | null>(
    () =>
      isMin1Period
        ? api.minute(instrument.code, effectiveDate ?? undefined)
        : Promise.resolve(null),
    [instrument.code, period, effectiveDate],
    // 盘中轮询当日分时 —— 后端 IntradayJob 每分钟落当日实时价
    { enabled: isMin1Period && !!effectiveDate, keepPrevious: true, pollMs: POLL_MS },
  );

  const minuteDaysQ = useAsync<MinuteDaysResponse | null>(
    () => (isMin5Period ? api.minuteDays(instrument.code, 5) : Promise.resolve(null)),
    [instrument.code, period],
    { enabled: isMin5Period, keepPrevious: true, pollMs: POLL_MS },
  );

  const klineQ = useAsync<{ bars: Bar[] }>(
    () => api.kline(instrument.code, klinePeriod(period), 2000),
    [instrument.code, period],
    // 盘中当日柱每分钟在变（is_complete=0），主副图数据同步轮询
    { enabled: !isMinutePeriod(period), pollMs: POLL_MS },
  );

  const quoteQ = useAsync(() => api.quotes([instrument.code]), [instrument.code], {
    keepPrevious: true,
    pollMs: POLL_MS,
  });
  const valQ = useAsync(
    () => api.valuation(instrument.code, 20),
    [instrument.code],
  );
  /**
   * 有日记的日期集合（主图标记用）—— 让"哪天写过笔记"一眼可见。
   *
   * ⚠️ 范围必须覆盖**图表自身的数据窗口**，而不是当前选中的那一天：
   * 日K 下 periodRange 只等于选中日，其它有笔记的日期根本不会进标记集合，
   * 标记会几乎全部消失（这正是此前方块画不出来的第二个原因）。
   * 库只会为存在于序列中的时间绘制标记，故取到柱范围即可，再宽也无用。
   */
  const bars = klineQ.data?.bars ?? [];
  const noteFrom = bars[0]?.date;
  const noteTo = bars[bars.length - 1]?.date;
  const notesQ = useAsync(
    () => api.notes(instrument.code, noteFrom ?? '0000-01-01', noteTo ?? '9999-12-31', 3000),
    [instrument.code, noteFrom, noteTo],
    { enabled: !!noteFrom && !!noteTo },
  );
  // 预测标记：同一柱区间。默认 limit 300 对日K 400 根柱截尾，显式放宽
  const predQ = useAsync(
    () => api.predictions(instrument.code, noteFrom ?? '0000-01-01', noteTo ?? '9999-12-31', 3000),
    [instrument.code, noteFrom, noteTo],
    { enabled: !!noteFrom && !!noteTo },
  );

  /** 有日记的日期及篇数（升序；归并到柱由 ChartCanvas 的 laneNotes 负责） */
  const noteDates = useMemo<{ date: string; count: number }[]>(() => {
    const m = new Map<string, number>();
    for (const n of notesQ.data?.items ?? []) m.set(n.scopeKey, (m.get(n.scopeKey) ?? 0) + 1);
    return [...m]
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [notesQ.data]);

  /** 有预测（情绪已设）的日期（升序；归并到柱由 ChartCanvas 的 lanePreds 负责）。
   *  情绪为空不标 —— 与复盘情绪时间线同一口径：没有情绪就没有颜色规则。 */
  const predDates = useMemo<{ date: string; sentiment: 'bullish' | 'bearish' | 'neutral' }[]>(() => {
    const s = new Set<'bullish' | 'bearish' | 'neutral'>(['bullish', 'bearish', 'neutral']);
    return (predQ.data?.items ?? [])
      .filter((p): p is typeof p & { sentiment: 'bullish' | 'bearish' | 'neutral' } =>
        typeof p.sentiment === 'string' && s.has(p.sentiment as 'bullish' | 'bearish' | 'neutral'))
      .map((p) => ({ date: p.date, sentiment: p.sentiment }))
      .sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [predQ.data]);

  /**
   * 点柱 → 查看对应的笔记与新闻。
   *
   * **不切周期**：周期不变，笔记 scope 才跟着图表粒度走
   * （scopeKey.ts 的 scopeOf：W→week、M→month、Y→year、D→day）。
   * 点周K柱就停在周K、记那周的周记 —— 这正是「看着周K 却在写日记」
   * 这个坑要避免的情形。
   *
   * 此前这里会在非日线时 setPeriod('D')，把选中的周期降级成日，
   * scope 随之变 day，既跳走了又丢掉周粒度，方向完全反了。
   *
   * ⚠️ 遗留：周K 柱的 date 是组内最后交易日，scopeKey 按 ISO 周推导，
   * 长假（如国庆）会让一个交易周横跨两个 ISO 周而漏掉假期前的交易日。
   * 与手动切周K 用筛选器时同样存在，属独立问题，未在此处理。
   */
  const handleSelectBar = useCallback((d: string | null) => {
    setDate(d);
  }, [setDate]);

  /** 选中的柱 = effectiveDate 本身（笔记 scope 的锚点），无需另存状态 */
  const selectedBarDate = effectiveDate;

  /* ── 派生读数（图例） ── */
  /**
   * 图例里的 MA 读数按**用户选中的周期**渲染。
   *
   * 改前硬编码只显示 MA5/10/20，而图表画出的是 5/10/20/60/120 ——
   * 二者不一致：图上明明有 MA60/MA120，图例却查不到数值。
   */
  /**
   * 读数条显示的柱：优先光标悬停的那根，否则末根（= 最新）。
   *
   * 光标可能落在两根柱之间的空隙，此时 param.time 是就近的一根；
   * 若它不在 bars 内（例如缩放后越界），则回落末根，绝不显示 undefined。
   */
  const crossBar = crossDate ? bars.find((b) => b.date === crossDate) ?? null : null;
  const activeBar = crossBar ?? bars.at(-1) ?? null;
  const crossActive = crossBar !== null;

  /**
   * 涨跌额/幅**自算**，不取 bar.change / bar.pctChange。
   *
   * 腾讯日 K 只有 6 个字段 [日期,开,收,高,低,量]，不含涨跌幅，解析越界取 nil
   * → 落库恒为 0（全库 25189 行中 23190 行为 0，见 §B.10）。直接显示会得到
   * 「收 3813.79 +0.00 +0.00%」这种自相矛盾的读数。
   * 用前一根收盘反算是唯一可靠口径；周/月/年线本就由 aggregate 按上一周期
   * 收盘算出，这里重算与之一致。
   */
  const [activeChange, activePct] = useMemo(() => {
    if (!activeBar) return [null, null] as const;
    const idx = bars.indexOf(activeBar);
    const prevClose = idx > 0 ? bars[idx - 1]!.close : undefined;
    if (prevClose === undefined || prevClose <= 0) {
      // 首根没有参照，退回后端字段（聚合周期在此有值）
      return [activeBar.change ?? null, activeBar.pctChange ?? null] as const;
    }
    const chg = activeBar.close - prevClose;
    return [chg, (chg / prevClose) * 100] as const;
  }, [activeBar, bars]);

  const closes = bars.map((b) => b.close);
  const maReadout = maPeriods
    .filter((n) => closes.length >= n)
    .map((n) => ({ n, v: sma(closes, n).at(-1) ?? null }))
    .filter((x): x is { n: MaPeriod; v: number } => x.v !== null);
  const bb = closes.length >= 20 ? bollinger(closes) : null;

  const quote = quoteQ.data?.items[0];
  const lastBar = bars.at(-1);
  const price = isMin1Period
    ? (minuteQ.data?.points.at(-1)?.price ?? quote?.price)
    : (lastBar?.close ?? quote?.price);
  const prevClose = quote?.prevClose ?? (bars.length >= 2 ? bars[bars.length - 2]!.close : undefined);
  const changePct =
    quote?.pctChange ??
    (price !== undefined && prevClose ? ((price - prevClose) / prevClose) * 100 : 0);
  /* 5 日分时：有效日（有点）——与 ChartCanvas 内的过滤条件保持一致 */
  const fiveDayValid = isMin5Period
    ? (minuteDaysQ.data?.days ?? []).filter((d) => d.points.length > 0)
    : [];

  /**
   * 分时光标悬停的分钟点 → 头部「点位/涨跌幅」跟随。
   * 单日在 minute.points 里找；5日按 dayIndex 定位到该日。
   * 涨跌幅参照：5日取该日自带 prevClose（各日 0% 归一化，与 5 日图注一致）；
   * 单日取 quote 昨收口径（分时默认即当日，无历史分时昨收字段）。
   */
  const crossMinuteHitt = useMemo(() => {
    if (!crossMinute) return null;
    const pt = isMin1Period
      ? (minuteQ.data?.points ?? []).find((p) => p.time === crossMinute.hhmm) ?? null
      : fiveDayValid[crossMinute.dayIndex]?.points.find((p) => p.time === crossMinute.hhmm) ?? null;
    if (!pt) return null;
    const ref = isMin5Period
      ? fiveDayValid[crossMinute.dayIndex]?.prevClose
      : prevClose;
    if (ref === undefined || ref <= 0) return { pt, chg: null as number | null, pct: null as number | null };
    return { pt, chg: pt.price - ref, pct: ((pt.price - ref) / ref) * 100 };
  }, [crossMinute, isMin1Period, minuteQ.data, fiveDayValid, prevClose]);
  const minuteHover = crossMinuteHitt !== null;

  /**
   * 头部数字派生：**光标目标优先，无光标回落到最新实时值**。
   *
   * K线（crossDate 命中柱）：点位=该柱收盘、涨跌幅=自算口径（activeChange/activePct）、
   * 高/低=该柱、成交=该柱 amount —— 历史成交额普遍缺失（可选字段、历史上为 0），
   * 缺则为 `-`，不用 quote 的当日值冒充悬停日。
   * 分时（悬停到分钟点，用户拍板口径）：点位/涨跌幅跟随，高/低/振幅/成交
   * 保持 quote 的当日全天值（日级统计）。
   */
  const displayPrice = minuteHover
    ? crossMinuteHitt.pt.price
    : isMinPeriodFlag ? price : (activeBar ? activeBar.close : price);
  const displayChange = minuteHover
    ? crossMinuteHitt.chg
    : isMinPeriodFlag
      // 分时无悬停 → 最新分钟价 vs 昨收（与原头部口径一致）
      ? (price !== undefined && prevClose && prevClose > 0 ? price - prevClose : null)
      : activeChange;
  const displayPct = minuteHover
    ? crossMinuteHitt.pct
    : isMinPeriodFlag ? changePct : activePct;
  const displayDir = upDown(displayPct ?? 0);
  const displayHigh = !isMinPeriodFlag && crossActive && activeBar ? activeBar.high : quote?.high;
  const displayLow = !isMinPeriodFlag && crossActive && activeBar ? activeBar.low : quote?.low;
  const displayAmp = !isMinPeriodFlag && crossActive && activeBar && prevClose && prevClose > 0
    ? ((activeBar.high - activeBar.low) / prevClose) * 100
    : quote?.amplitude;
  const displayAmount = !isMinPeriodFlag && crossActive && activeBar
    ? (activeBar.amount && activeBar.amount > 0 ? activeBar.amount : null)
    : quote?.amount;

  const valLatest: ValuationPoint | null = valQ.data?.latest ?? null;
  const valSupported = valQ.data?.supported ?? false;
  /*
   * 图表区高度**直接取档位值**，不再实测占位盒。
   *
   * 改前是 ResizeObserver 实测（因为要 flex-1 填满剩余空间）。改为整页滚动 +
   * 固定高度后，高度由预设决定、属于确定值，实测反而带来两个问题：
   *   · 首帧 canvasH=0 → 图表晚一帧才出现（闪一下空白）
   *   · 实测值与 CSS 高度若因舍入差 1px，就会重演"固定高度与实测不一致
   *     导致图表底部被裁"的老问题（曾两次发生，见提交 9071f60 / 95eee42）
   */
  const effectivePanes = isMinPeriodFlag ? minutePanes : panes;
  const activePanes = effectivePanes.filter((p): p is PaneId => p !== null);
  const space = solveChartSpace(chartH - READOUT_HEIGHT, activePanes.length);
  const loading = isMin1Period
    ? minuteQ.status === 'loading'
    : isMin5Period
      ? minuteDaysQ.status === 'loading'
      : klineQ.status === 'loading';
  const error = isMin1Period ? minuteQ.error : isMin5Period ? minuteDaysQ.error : klineQ.error;

  const themeColors = getChartTheme(theme);
  const bandColors = themeColors.band;

  /* 分时 unavailable → 明确引导至日K，不静默显示空图 */
  const minuteUnavailable = isMin1Period
    ? minuteQ.data?.precision === 'unavailable'
    : isMin5Period && !!minuteDaysQ.data && minuteDaysQ.data.days.every((d) => d.points.length === 0);

  return (
// shrink-0：图表区高度由档位固定，不再 flex-1 撑满视口 ——
    // 整页滚动下工作区紧随其后，用户可以一路滑到笔记与新闻。
    // 注：换高度档的双击手势在 ChartCanvas 的绘图区根节点上（作用域收窄），
    // 指数头部/周期 tab/读数条不再误触发。
    <div className="flex shrink-0 flex-col">
      {/* 指数头部 */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-b px-3 py-2">
        <div className="flex items-baseline gap-2">
          {/* 指数名本身即选择器：顶栏快切只有 7/13 且 <1024px 整条隐藏 */}
          <InstrumentPicker />
          <span className="num text-[11px]" style={{ color: 'var(--text-3)' }}>{instrument.code}</span>
          {instrument.market === 'HK' && (
            <span className="rounded px-1 py-0.5 text-[10px]"
              style={{ background: 'var(--bg-elevated)', color: 'var(--text-3)' }}>港股</span>
          )}
          {EASTMONEY_ONLY.has(instrument.code) && (
            <span className="rounded px-1 py-0.5 text-[10px]"
              style={{ background: 'var(--bg-elevated)', color: 'var(--warn)' }}>仅东财</span>
          )}
        </div>

        {displayPrice !== undefined && displayPrice > 0 ? (
          <>
            <div className="flex items-baseline gap-2">
              <span className="num text-[26px] font-semibold leading-none">
                {displayPrice.toFixed(2)}
              </span>
              {displayChange !== null && (
                <span className={`num text-[13px] ${displayDir}`}>
                  {(displayChange >= 0 ? '+' : '')}{displayChange.toFixed(2)}
                </span>
              )}
              <span className={`num text-[15px] font-semibold ${displayDir}`}>
                {(displayPct ?? 0) >= 0 ? '+' : ''}{(displayPct ?? 0).toFixed(2)}%
              </span>
            </div>
            <div className="flex items-center gap-3 text-[12px]" style={{ color: 'var(--text-2)' }}>
              {num(displayHigh) !== null && <span>高 {num(displayHigh)!.toFixed(2)}</span>}
              {num(displayLow) !== null && <span>低 {num(displayLow)!.toFixed(2)}</span>}
              {num(displayAmp) !== null && <span>振幅 {num(displayAmp)!.toFixed(2)}%</span>}
              {/* K线悬停时历史成交额普遍缺失 → 显示 -；quote 路径与此前一致 */}
              <span>成交 {num(displayAmount) !== null ? fmtAmount(num(displayAmount)!) : '-'}</span>
            </div>
          </>
        ) : (
          <span className="text-[12px]" style={{ color: 'var(--warn)' }}>
            {loading ? '加载中…' : instrument.code === '932000' ? '⚠ 仅东财支持，遇限流时不可用' : '暂无快照'}
          </span>
        )}

        {/*
          数据源徽标按需求移除。
          保留了「快照不可用」等真正需要提示的状态 —— 那是错误，不是来源说明。
          需要排查数据来源时：状态栏或 /api/status 仍能拿到 source。
        */}
      </div>

      {/* 周期 + 指标 */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-1.5"
        style={{ background: 'var(--bg-surface)' }}>
        <PeriodTabs />
        <span className="mx-1 h-4 w-px" style={{ background: 'var(--border)' }} />
        {/* 不再加「叠加」「副图」文字标签：按钮自身已带 label 与计数，
            外部标签只是重复信息，还占掉横向空间（窄屏尤甚） */}
        <OverlayConfig
          active={overlays}
          onChange={setOverlays}
          maPeriods={maPeriods}
          onMaPeriodsChange={changeMaPeriods}
          valuationBasis={valBasis}
          onValuationBasisChange={setValBasis}
          valuationKind={valKind}
          onValuationKindChange={setValKind}
          valuationSupported={valSupported}
        />
        <PaneSelector
          value={effectivePanes}
          onChange={(next) => (isMinPeriodFlag ? setMinutePanes(next) : setPanes(next))}
          available={isMinPeriodFlag ? ['VOL'] : undefined}
        />
      </div>

      {/* 读数条 —— 光标驱动；估值无右轴刻度，这里也是 PE 的唯一读数途径 */}
      <ReadoutBar bar={activeBar} change={activeChange} pct={activePct} isLive={!crossActive}>
        {!isMinutePeriod(period) && maReadout.length > 0 && (
          <>
            <span className="h-3 w-px shrink-0" style={{ background: 'var(--border)' }} />
            {maReadout.map(({ n, v }) => (
              // 用与图上线条相同的配色，读数与线一一对应
              <span key={n} className="shrink-0" style={{ color: themeColors.ma[n] }}>
                MA{n} {v.toFixed(2)}
              </span>
            ))}
          </>
        )}
        {bb && (
          <>
            <span className="h-3 w-px shrink-0" style={{ background: 'var(--border)' }} />
            <span className="shrink-0" style={{ color: 'var(--text-3)' }}>BOLL</span>
          </>
        )}
        {valSupported ? (
          <>
            <span className="h-3 w-px shrink-0" style={{ background: 'var(--border)' }} />
            {num(valLatest?.pe2) !== null && (
              <span className="shrink-0" style={{ color: 'var(--text-1)' }}>PE(TTM) {num(valLatest?.pe2)!.toFixed(2)}</span>
            )}
            {num(valLatest?.pe1) !== null && (
              <span className="shrink-0">PE(静) {num(valLatest?.pe1)!.toFixed(2)}</span>
            )}
            {num(valLatest?.divYield2) !== null && (
              <span className="shrink-0">股息率 {num(valLatest?.divYield2)!.toFixed(2)}%</span>
            )}
            {valLatest?.pe2Rank !== null && valLatest?.pe2Rank !== undefined && (
              <span className="shrink-0" style={{ color: 'var(--text-3)' }}>分位 {valLatest.pe2Rank}%</span>
            )}
            <span className="shrink-0 text-[11px]" style={{ color: 'var(--text-3)' }}>
              [中证 · {valBasis === 'pe1' ? '静态' : 'TTM'}
              {valKind === 'both' && ' · 含股息率'} · 20日]
            </span>
          </>
        ) : (
          <span className="shrink-0 text-[11px]" style={{ color: 'var(--text-3)' }}>
            [该指数不在中证估值体系]
          </span>
        )}
      </ReadoutBar>

      {/* 图表 */}
      {/* shrink-0 而非 flex-1：高度由下方占位盒的固定值决定，
          若这里 flex-1 会去吃父级剩余空间，与"图表高度=档位"的语义冲突 */}
      <div className="flex shrink-0 flex-col overflow-hidden">
        {error && (
          <div className="shrink-0 px-3 py-1.5 text-[12px]"
            style={{ background: 'var(--bg-elevated)', color: 'var(--warn)' }}>
            ⚠ {error}
          </div>
        )}
        {minuteUnavailable && (
          <div className="shrink-0 px-3 py-1.5 text-[12px]"
            style={{ background: 'var(--bg-elevated)', color: 'var(--warn)' }}>
            ⚠ 该日期已超分时可恢复窗口（≤29 个交易日），请改用日K 查看 —— 不提供伪造数据
          </div>
        )}
        {isMin1Period && minuteQ.data && minuteQ.data.precision !== 'unavailable' && (
          <div className="shrink-0 px-3 py-1 text-[11px]" style={{ color: 'var(--text-3)' }}>
            {minuteQ.data.date} · {minuteQ.data.points.length} 点 ·{' '}
            {minuteQ.data.precision === 'tick' ? '真实分时'
              : `由 ${minuteQ.data.precision} 分钟线重建（精度下降）`}
            {' · 均价线不可用（腾讯成交量单位为「手」，与成交额不同量纲）'}
          </div>
        )}

        {/* 5 日分时图例：每天一色，标注日期与当日涨跌幅（相对各自昨收） */}
        {isMin5Period && fiveDayValid.length > 0 && (
          <div className="num flex shrink-0 flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-1 text-[11px]"
            style={{ color: 'var(--text-2)' }}>
            <span style={{ color: 'var(--text-3)' }}>
              5 日分时 · {fiveDayValid[0]!.date.slice(5)} ~ {fiveDayValid[fiveDayValid.length - 1]!.date.slice(5)}
            </span>
            {fiveDayValid.map((d, i) => {
              const last = d.points[d.points.length - 1]?.price;
              const chg = d.prevClose > 0 && last !== undefined ? (last / d.prevClose - 1) * 100 : null;
              return (
                <span key={d.date} className="flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-[2px]"
                    style={{ background: bandColors[i % bandColors.length] }} />
                  {d.date.slice(5)}
                  {chg !== null && (
                    <span className={upDown(chg)}>
                      {chg >= 0 ? '+' : ''}{chg.toFixed(2)}%
                    </span>
                  )}
                </span>
              );
            })}
          </div>
        )}

        {/* 固定高度 + overflow-hidden：图表区不再随窗口拉伸，也不把下方内容顶出视口 */}
        <div className="shrink-0 overflow-hidden" style={{ height: chartH }}>
          <ChartCanvas
              bars={bars}
              minute={minuteQ.data}
              minuteDays={isMin5Period ? minuteDaysQ.data?.days : undefined}
              market={instrument.market}
              period={period}
              theme={theme}
              overlays={overlays as Set<string>}
              maPeriods={maPeriods}
              onCyclePreset={onCyclePreset}
              onCrosshairBar={setCrossDate}
              onCrosshairMinute={handleCrossMinute}
              selectedDate={selectedBarDate}
              noteDates={noteDates}
              predDates={predDates}
              onSelectDate={handleSelectBar}
              panes={effectivePanes}
              height={chartH}
              prevClose={prevClose ?? null}
              valuation={valQ.data?.items}
              valuationBasis={valBasis}
              valuationKind={valKind}
            />
        </div>
      </div>

      {space.cramped && (
        <div className="shrink-0 px-3 py-1 text-[11px]"
          style={{ background: 'var(--bg-elevated)', color: 'var(--warn)' }}>
          {space.paneHeight > 0
            ? `图表高度紧张（主图降至 ${Math.round(space.mainHeight)}px，低于建议的 ${MAIN_CHART_MIN}px）。双击图表区可切换到更高的档位。`
            : `图表高度紧张，${activePanes.length} 个副图已让位给主图（现 ${Math.round(space.mainHeight)}px）。双击图表区切换到「图表优先」档位可恢复副图。`}
        </div>
      )}
    </div>
  );
}

/** 数值守卫：上游字段可能缺失/null，渲染前统一过滤，杜绝 undefined.toFixed */
function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * 成交额格式化。**入参单位是万元**（腾讯快照 [37] 的实际单位），
 * 以万为基准换算 —— 按「元」处理会差 1e4 倍。
 */
function fmtAmount(wan: number): string {
  if (wan >= 1e8) return `${(wan / 1e4).toFixed(0)}万亿`;
  if (wan >= 1e4) return `${(wan / 1e4).toFixed(0)}亿`;
  return `${wan.toFixed(0)}万`;
}